import {useMaterialStorageDataQuery} from '@domain/material-storage'
import {useMaterials, mapToMaterialsById} from '@domain/materials'
import {useStockDevelopmentQuery} from '@domain/schedule'
import {dataTestId} from '@hconnect/uikit'
import {useUpmPlantTimezone, useUpmPlantContext} from '@hconnect/upm'
import {Skeleton} from '@mui/material'
import {StockCard, StockDevelopmentChartSkeleton} from '@widgets/stock-card'
import React from 'react'

import {usePlanRange} from '../../hooks'
import {useMaterialOrderPermissions} from '../../providers'
import {getListOfDays} from '../../utils'

import {StaticStockDevelopmentChart} from './StaticStockDevelopmentChart'

interface StockDevelopmentCardProps {
  materialId: number
  // to get correct query data
  materialIds: number[]
  stockMaterialsOrdersUrl: string
}

export const SimpleStockDevelopmentCard: React.FC<StockDevelopmentCardProps> = ({
  materialId,
  materialIds,
  stockMaterialsOrdersUrl
}) => {
  const timezoneId = useUpmPlantTimezone()
  const planRange = usePlanRange()
  const {plantId: plantCode} = useUpmPlantContext()
  const {canViewMaterialOrders} = useMaterialOrderPermissions()
  const {data: materialsById} = useMaterials({
    plantCode,
    options: {
      select: mapToMaterialsById
    }
  })
  const {data: stockDevelopment, isLoading} = useStockDevelopmentQuery({
    materialIds,
    timeframe: planRange
  })
  const {data: storageData} = useMaterialStorageDataQuery({materialId})
  const selectedMaterial = materialsById?.[materialId]

  const listOfDays = getListOfDays({start: planRange[0], end: planRange[1]})

  const shouldShowChartSkeleton = isLoading || !stockDevelopment

  return (
    <StockCard
      stockMaterialsOrdersUrl={stockMaterialsOrdersUrl}
      page="material"
      canViewMaterialOrders={canViewMaterialOrders}
      title={
        selectedMaterial?.name ?? (
          <Skeleton variant="text" sx={{width: ({spacing}) => spacing(30)}} />
        )
      }
      material={selectedMaterial}
      {...dataTestId(`stock_development_card_${selectedMaterial?.id}`)}
    >
      {shouldShowChartSkeleton ? (
        <StockDevelopmentChartSkeleton />
      ) : (
        <StaticStockDevelopmentChart
          stockData={stockDevelopment[materialId]}
          listOfDays={listOfDays}
          timezoneId={timezoneId}
          minTargetLevels={
            storageData
              ? {
                  week: storageData.minTargetLevel,
                  weekend: storageData.minTargetLevelWeekend
                }
              : undefined
          }
          storageCapacity={storageData?.storageCapacity}
        />
      )}
    </StockCard>
  )
}
